import React, { useCallback, useEffect, useState } from 'react'
import { GridColumn as Column, Grid, GridColumnProps } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { MarketingCampaignDetailDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { LuckyDrawResultDto } from 'api/Trading-Accounts/Types/MarketingCampaignResultsDto'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { getCampaignResultFields } from 'components/Filter/CampaignFields/getCampaignResultFields'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { createGridState, useGridFilter, useGridState, useGridStatusState } from 'features/DataGrid'
import { isGoBack } from 'lib/react-router-dom'
import { CompositeFilterDescriptor, GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { buildQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { CampaignTab } from './CampaignSlug'
import { CampaignNavigation } from './Navigation/CampaignNavigation'

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

interface OuterProps {
  data: MarketingCampaignDetailDto
  columns: GridColumn[]
}

export const CampaignRewardsResultsPage: React.FC<OuterProps> = ({ data, ...props }) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [campaignResults, setCampaignResults] = useState<LuckyDrawResultDto[]>([])

  const { gridState, gridActions } = useGridState()
  const { gridStatusActions } = useGridStatusState()
  const gridFilter = useGridFilter(gridStatusActions)

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      if (data === undefined) return null
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter, data]
  )

  const fetchGrid = useCallback(
    async (
      callback: (gridFilterState: GridFilterActionState) => Promise<PageData<LuckyDrawResultDto[]>>
    ) => {
      gridStatusActions.setLoading(true)
      const gridFilterState = gridFilter.selectFilterForGoBackHistory(
        createGridState({ skip: 0 }, gridState, { isGoBack })
      )
      const response = await callback(gridFilterState)
      if (response?.totalResults >= 0) {
        gridActions.setGridFilters({ total: response.totalResults })
      }
      gridStatusActions.setLoading(false)
    },
    [gridFilter, gridActions, gridStatusActions, gridState]
  )

  useEffect(() => {
    ;(async () => {
      await fetchGrid(async (gridFilterState) => {
        const search = getQuery(gridFilterState.filter)
        const pageIndex = gridFilterState.pageIndex ?? 1
        const pageSize = gridFilterState.pageSize ?? 10
        const response = await tradingAccountsApiClient.getLuckyDrawResults(data.id, {
          search,
          pageIndex,
          pageSize,
        })
        setCampaignResults(response?.items ?? [])
        setTableData({ ...tableData, meta: response })
        return response
      })
    })()
  }, [data.id])

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const search = params?.query !== undefined ? params.query : getQuery()
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await tradingAccountsApiClient.getLuckyDrawResults(data.id, {
        search,
        pageIndex,
        pageSize,
      })
      const total = response?.totalResults ?? 0
      setCampaignResults(response?.items ?? [])
      gridActions.setGridFilters({ take, skip, total, pageIndex })
      setTableData({ ...tableData, meta: response })
    },
    [
      getQuery,
      gridActions,
      gridState.pageSize,
      gridState.take,
      tableData,
      tradingAccountsApiClient,
      data.id,
    ]
  )

  const handleGridPageChange = async (page: number) => {
    const pageIndex = page
    const pageSize = gridState.take ?? 10

    gridActions.setGridFilters({ pageIndex })

    const response = await tradingAccountsApiClient.getLuckyDrawResults(data.id, {
      search: getQuery(),
      pageIndex,
      pageSize,
    })

    if (response?.items) {
      setCampaignResults(response.items)
    } else {
      setCampaignResults([])
    }

    gridActions.setGridFilters({
      total: response.totalResults ?? 0,
      pageIndex,
    })
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    gridActions.setGridFilters({
      pageIndex: 1,
      pageSize,
      take: pageSize,
      skip: 0,
    })

    const response = await tradingAccountsApiClient.getLuckyDrawResults(data.id, {
      search: getQuery(),
      pageIndex: 1,
      pageSize,
    })

    if (response?.items) {
      setCampaignResults(response.items)
    } else {
      setCampaignResults([])
    }

    gridActions.setGridFilters({
      pageIndex: 1,
      pageSize,
      take: pageSize,
      total: response.totalResults ?? 0,
    })
    setTableData({ ...tableData, meta: response })
  }

  return (
    <>
      <CampaignNavigation data={data} tab={CampaignTab.RESULTS} />
      <SelectFiltersFacade
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.CampaignMarginBonusResults}
        gridState={gridState}
        filterFields={getCampaignResultFields(data.type.id)}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <TableWithExport data={campaignResults} columns={props.columns}>
          <Grid
            resizable
            sortable={false}
            data={campaignResults}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            pageSize={gridState.pageSize}
            pager={() => (
              <Pager
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take}
                skip={gridState.skip}
              />
            )}
            pageable={pageableSettings}
            style={{ zIndex: 0 }}
          >
            {props.columns
              .filter((c) => c.show)
              .map((column, idx) => (
                <Column key={idx + 2} {...column} />
              ))}
          </Grid>
        </TableWithExport>
      </TableStateContext.Provider>
    </>
  )
}
